import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = not authenticated, object = user
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        // Iter-32 — Skip /me check while the new /auth/google route is processing the OAuth code,
        // GoogleCallback will hydrate the user itself once the exchange succeeds.
        if (window.location.pathname === "/auth/google") {
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        return data;
    };

    const register = async (email, password, name, inviteToken, extra = {}) => {
        const { data } = await api.post("/auth/register", {
            email,
            password,
            name,
            invite_token: inviteToken || undefined,
            ...extra,
        });
        setUser(data);
        return data;
    };

    const logout = () => {
        // Iter-33 — Déconnexion robuste :
        //   1. Vide le user en mémoire (fait tomber ProtectedRoute immédiatement).
        //   2. Notifie le backend en arrière-plan (fire-and-forget, timeout court).
        //   3. HARD REDIRECT vers /login (window.location) plutôt que react-router :
        //      élimine toute course avec les useEffect en cours sur la page
        //      courante (bug reproduit uniquement sur certaines pages en prod).
        setUser(false);
        api.post("/auth/logout", null, { timeout: 8000 }).catch(() => {});
        window.location.href = "/login";
    };

    const refreshUser = async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {}
    };

    return (
        <AuthContext.Provider
            value={{ user, loading, login, register, logout, refreshUser, setUser }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}
